/**
 * MAILBOX-UNIT account manager (2026-09-19 operatör isteği) — ONE shared card
 * adopted by all 11 mailbox-unit provider pages (rambler, aol, web.de, libero,
 * tiscali.it, tiscali.cz, vip.sina.com, sina.com, gazeta.pl, rediffmail,
 * t-online.de). Each mailbox is ONE sellable unit; there is no alias minting and
 * no renewal pool. This card gives the operator per-mailbox account tools on top
 * of the read-only site-usage view the pages already show.
 *
 * Data: the whole table + search/sort is driven by the SAME read model the page
 * uses — GET /admin/providers/:id/site-usage (mailboxes[]). Every mutation nests
 * under the existing /parents Prefix (D-225-safe). On any successful mutation the
 * card reloads its own rows AND calls the page's onChanged() so the KPI cards +
 * per-site table refresh too.
 *
 * SAFETY (non-negotiable): a mailbox that is reserved or leased is never
 * disabled/deleted/re-verified out from under a live order — the server refuses
 * with 409 and the card surfaces the Turkish reason. Default "Sil" is a
 * status-based SOFT delete that preserves every row (order forensics, site-usage
 * burns, ledger stay intact); a HARD delete is offered only for a pristine
 * mailbox, needs provider:drain, and the operator must type the accountId.
 *
 * SECRET HYGIENE: the password is never in the list, CSV, log or URL. Only the
 * explicit, audited "Şifre göster" action fetches it; it is shown once, offered
 * for copy, and never persisted. The ADDRESS is masked for display only (it is
 * not a credential) with a per-row client toggle.
 */
import { useMemo, useState } from 'react';

import { resolveBaseUrl, type ApiClientOptions, type ApiResult } from '../../api/client.js';
import { useApiQuery } from '../../api/hooks.js';
import { useCan } from '../../session/can.js';
import { Badge, Button, Card, CardBody, CardHeader, Icon, Input, Modal, Select, Tabs } from '../../ui/index.js';
import {
  bulkReprobe,
  bulkTakeOffSale,
  deleteMailbox,
  downloadCsv,
  exportCsv,
  fetchMailboxUnits,
  isPristineRow,
  leaseStateLabel,
  maskAddress,
  reasonText,
  reprobe,
  resetQuarantine,
  revealPassword,
  setSaleStatus,
  statusLabel,
  toMailboxRows,
  updatePassword,
  type MailboxRow,
  type MailboxTone,
  type MailboxUnitProviderId,
  type MailboxUnitReadModel,
} from './mailboxUnitApi.js';

export interface MailboxUnitManagerProps {
  readonly providerId: MailboxUnitProviderId;
  /** Operator-facing provider name, e.g. "rambler.ru". */
  readonly providerLabel: string;
  /** Called after any successful mutation so the page can refresh its KPIs. */
  readonly onChanged?: () => void;
}

type StatusFilter = 'all' | 'active' | 'login_failed' | 'suspended' | 'disabled';
type SortKey = 'address' | 'status' | 'lease' | 'lastSeen' | 'errors';

type Feedback = { readonly tone: MailboxTone; readonly text: string };

type ModalState =
  | { readonly kind: 'password'; readonly row: MailboxRow }
  | { readonly kind: 'delete'; readonly row: MailboxRow }
  | { readonly kind: 'sale'; readonly row: MailboxRow; readonly onSale: boolean }
  | { readonly kind: 'quarantine'; readonly row: MailboxRow }
  | { readonly kind: 'bulkTakeOff' }
  | null;

const MIN_REASON = 4;
const MASK = '•••';

function fmt(ts: string | null | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('tr-TR');
}

function rowKey(row: MailboxRow): string {
  return row.accountId || row.aliasId || row.address;
}

/** Map any non-ok ApiResult to Turkish operator text (never leaks a value). */
function apiError(res: ApiResult<unknown>): string {
  if (res.kind === 'forbidden') {
    return `Yetki yok: ${res.missingCapability ?? 'gerekli izin'} hesabınızda yok.`;
  }
  if (res.kind === 'auth_required') return reasonText('auth_required');
  if (res.kind === 'network_error') return reasonText(`network:${res.message}`);
  if (res.kind === 'error') return reasonText(res.reason);
  return 'İşlem başarısız.';
}

/**
 * Outer gate: reading the manager needs provider:inspect. Without it the card
 * does not render at all (the page's KPI read would also be denied). The
 * capability flags are resolved here and passed down so the inner component's
 * hook order never changes when the session resolves.
 */
export function MailboxUnitManager(props: MailboxUnitManagerProps): JSX.Element | null {
  const canInspect = useCan('provider:inspect');
  const canManage = useCan('provider:manage_inventory');
  const canDrain = useCan('provider:drain');
  if (!canInspect) return null;
  return <MailboxUnitManagerInner {...props} canManage={canManage} canDrain={canDrain} />;
}

function MailboxUnitManagerInner({
  providerId,
  providerLabel,
  onChanged,
  canManage,
  canDrain,
}: MailboxUnitManagerProps & { readonly canManage: boolean; readonly canDrain: boolean }): JSX.Element {
  const query = useApiQuery<MailboxUnitReadModel>((o) => fetchMailboxUnits(o, providerId), [providerId]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('address');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [revealAddr, setRevealAddr] = useState<ReadonlySet<string>>(new Set());
  const [revealed, setRevealed] = useState<{ readonly key: string; readonly email: string; readonly password: string } | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);

  const rows = useMemo<MailboxRow[]>(
    () => (query.kind === 'ok' ? toMailboxRows(query.data.mailboxes) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.kind, query.version],
  );

  const visible = useMemo<MailboxRow[]>(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (q && !r.address.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      return true;
    });
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      switch (sortKey) {
        case 'status':
          return a.status.localeCompare(b.status) || a.address.localeCompare(b.address);
        case 'lease':
          return a.leaseState.localeCompare(b.leaseState) || a.address.localeCompare(b.address);
        case 'errors':
          return b.consecutiveErrors - a.consecutiveErrors || a.address.localeCompare(b.address);
        case 'lastSeen': {
          const ta = a.lastSeenAt ? Date.parse(a.lastSeenAt) : -Infinity;
          const tb = b.lastSeenAt ? Date.parse(b.lastSeenAt) : -Infinity;
          return tb - ta || a.address.localeCompare(b.address);
        }
        case 'address':
        default:
          return a.address.localeCompare(b.address);
      }
    });
    return sorted;
  }, [rows, search, statusFilter, sortKey]);

  const selectedIds = useMemo(
    () => visible.filter((r) => selected.has(rowKey(r)) && r.accountId).map((r) => r.accountId),
    [visible, selected],
  );

  function toggleSelect(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleSelectAll(): void {
    setSelected((prev) => {
      const allKeys = visible.map(rowKey);
      const allSelected = allKeys.length > 0 && allKeys.every((k) => prev.has(k));
      return allSelected ? new Set() : new Set(allKeys);
    });
  }

  function options(): ApiClientOptions {
    return { baseUrl: resolveBaseUrl() };
  }

  /** Run a mutation, refresh on success, and surface a Turkish message. */
  async function run<T>(
    key: string,
    fn: (o: ApiClientOptions) => Promise<ApiResult<T>>,
    successText: string,
  ): Promise<boolean> {
    setBusy(key);
    setFeedback(null);
    const res = await fn(options());
    setBusy(null);
    if (res.kind === 'ok') {
      setFeedback({ tone: 'success', text: successText });
      query.reload();
      onChanged?.();
      return true;
    }
    setFeedback({ tone: 'danger', text: apiError(res) });
    return false;
  }

  async function doReveal(row: MailboxRow): Promise<void> {
    setOpenMenu(null);
    setBusy(`reveal:${rowKey(row)}`);
    setFeedback(null);
    const res = await revealPassword(options(), providerId, row.accountId);
    setBusy(null);
    if (res.kind === 'ok') {
      setRevealed({ key: rowKey(row), email: res.data.email, password: res.data.password });
      return;
    }
    setFeedback({ tone: 'danger', text: apiError(res) });
  }

  async function copyText(text: string): Promise<void> {
    try {
      await navigator.clipboard?.writeText(text);
      setFeedback({ tone: 'success', text: 'Panoya kopyalandı.' });
    } catch {
      setFeedback({ tone: 'warn', text: 'Kopyalanamadı — elle seçip kopyalayın.' });
    }
  }

  async function doReprobe(row: MailboxRow): Promise<void> {
    setOpenMenu(null);
    await run(`reprobe:${rowKey(row)}`, (o) => reprobe(o, providerId, row.accountId), 'Yeniden deneme sıraya alındı — en geç 1 dakikada doğrulanır.');
  }

  async function doBulkReprobe(): Promise<void> {
    if (selectedIds.length === 0) return;
    const ok = await run('bulk:reprobe', (o) => bulkReprobe(o, providerId, selectedIds), 'Seçilenler yeniden denemeye alındı.');
    if (ok) setSelected(new Set());
  }

  async function doBulkCsv(): Promise<void> {
    setBusy('bulk:csv');
    setFeedback(null);
    const res = await exportCsv(options(), providerId, selectedIds.length > 0 ? selectedIds : undefined);
    setBusy(null);
    if (res.kind === 'ok') {
      downloadCsv(res.data.csvBase64, `${providerId}-mailboxes-${new Date().toISOString().slice(0, 10)}.csv`);
      setFeedback({ tone: 'success', text: `CSV indirildi (${res.data.rowCount} satır, gizli sütun yok).` });
      return;
    }
    setFeedback({ tone: 'danger', text: apiError(res) });
  }

  const anySelected = selectedIds.length > 0;
  const inputsDisabled = busy !== null;

  return (
    <Card flush data-testid="mailbox-unit-manager" data-provider={providerId} style={{ marginBottom: 16 }}>
      <CardHeader
        title="Hesap yönetimi"
        subtitle={
          <>
            {providerLabel} mailbox'larını buradan yönetin: şifre göster/değiştir, yeniden dene,
            karantinadan çıkar, satıştan kaldır/aç ve sil. Rezerve veya kirada olan bir mailbox,
            canlı sipariş kapanmadan devre dışı bırakılamaz veya silinemez.
          </>
        }
        actions={
          <div className="mbx-controls">
            <Input
              data-testid="mbx-search"
              placeholder="adres ara"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              iconLeft={<Icon.Search size={14} />}
              style={{ minWidth: 160 }}
            />
            <Select
              data-testid="mbx-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              aria-label="Duruma göre filtrele"
            >
              <option value="all">Tüm durumlar</option>
              <option value="active">Aktif</option>
              <option value="login_failed">Giriş başarısız</option>
              <option value="suspended">Askıda</option>
              <option value="disabled">Devre dışı</option>
            </Select>
            <Select
              data-testid="mbx-sort"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              aria-label="Sırala"
            >
              <option value="address">Adrese göre</option>
              <option value="status">Duruma göre</option>
              <option value="lease">Kiraya göre</option>
              <option value="lastSeen">Son kontrole göre</option>
              <option value="errors">Hataya göre</option>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              data-testid="mbx-refresh"
              onClick={() => query.reload()}
              icon={<Icon.Refresh size={14} />}
              aria-label="Yenile"
            >
              Yenile
            </Button>
          </div>
        }
      />
      <CardBody>
        {feedback ? (
          <div
            data-testid="mbx-feedback"
            role={feedback.tone === 'danger' ? 'alert' : 'status'}
            className={`mbx-feedback mbx-feedback-${feedback.tone}`}
          >
            {feedback.text}
          </div>
        ) : null}

        {anySelected ? (
          <div className="mbx-bulk-bar" data-testid="mbx-bulk-bar">
            <span className="mbx-bulk-count">{selectedIds.length} seçili</span>
            <Button
              variant="secondary"
              size="sm"
              data-testid="mbx-bulk-csv"
              onClick={() => void doBulkCsv()}
              loading={busy === 'bulk:csv'}
              icon={<Icon.Doc size={13} />}
              title="Yalnız gizli olmayan sütunlar — şifre asla dışa aktarılmaz"
            >
              Seçilenleri CSV indir
            </Button>
            {canManage ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="mbx-bulk-reprobe"
                  onClick={() => void doBulkReprobe()}
                  loading={busy === 'bulk:reprobe'}
                  icon={<Icon.Refresh size={13} />}
                >
                  Seçilenleri yeniden dene
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  data-testid="mbx-bulk-takeoff"
                  onClick={() => setModal({ kind: 'bulkTakeOff' })}
                  icon={<Icon.Cancel size={13} />}
                >
                  Seçilenleri satıştan kaldır
                </Button>
              </>
            ) : null}
            <span className="mbx-bulk-note">gizli sütun yok</span>
          </div>
        ) : null}

        {query.kind === 'idle' || query.kind === 'loading' ? (
          <div className="t-small t-tertiary" data-testid="mbx-loading">Yükleniyor…</div>
        ) : query.kind === 'auth_required' ? (
          <div role="alert" data-testid="mbx-error">{reasonText('auth_required')}</div>
        ) : query.kind === 'error' ? (
          <div role="alert" data-testid="mbx-error">
            <Badge tone="danger" style={{ marginRight: 8 }}>Hata</Badge>
            Mailbox'lar okunamadı:{' '}
            {query.reason === 'forbidden'
              ? `Yetki yok: ${query.missingCapability ?? 'provider:inspect'} izni gerekiyor.`
              : reasonText(query.reason)}
          </div>
        ) : visible.length === 0 ? (
          <div className="t-small t-tertiary" data-testid="mbx-empty">
            {rows.length === 0 ? 'Henüz mailbox yok — Import / TXT ile ekleyin.' : 'Filtreye uyan mailbox yok.'}
          </div>
        ) : (
          <div className="tbl-wrap" style={{ overflowX: 'auto' }}>
            <table className="tbl mbx-table" data-testid="mbx-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      data-testid="mbx-select-all"
                      aria-label="Tümünü seç"
                      checked={visible.length > 0 && visible.every((r) => selected.has(rowKey(r)))}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Maskeli adres</th>
                  <th>Durum</th>
                  <th>Oturum</th>
                  <th>Kira</th>
                  <th>Ard. hata</th>
                  <th>Son kontrol</th>
                  <th>Sunulan / Yanan siteler</th>
                  {canManage ? <th style={{ width: 96 }}>İşlemler</th> : null}
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const key = rowKey(row);
                  const st = statusLabel(row.status);
                  const lease = leaseStateLabel(row.leaseState);
                  const addrShown = revealAddr.has(key) ? row.address : maskAddress(row.address);
                  const isRevealed = revealed?.key === key;
                  return (
                    <tr key={key} data-testid={`mbx-row-${key}`}>
                      <td data-label="Seç">
                        <input
                          type="checkbox"
                          data-testid={`mbx-select-${key}`}
                          aria-label={`${row.address} seç`}
                          checked={selected.has(key)}
                          onChange={() => toggleSelect(key)}
                        />
                      </td>
                      <td className="t-mono" data-label="Maskeli adres">
                        <span className="mbx-addr">
                          <span data-testid={`mbx-address-${key}`}>{addrShown}</span>
                          <button
                            type="button"
                            className="iconbtn mbx-addr-toggle"
                            data-testid={`mbx-address-toggle-${key}`}
                            aria-label={revealAddr.has(key) ? 'Adresi gizle' : 'Adresi göster'}
                            onClick={() =>
                              setRevealAddr((prev) => {
                                const next = new Set(prev);
                                if (next.has(key)) next.delete(key);
                                else next.add(key);
                                return next;
                              })
                            }
                          >
                            {revealAddr.has(key) ? <Icon.EyeOff size={13} /> : <Icon.Eye size={13} />}
                          </button>
                        </span>
                        {isRevealed ? (
                          <div className="mbx-revealed" data-testid={`mbx-revealed-${key}`} role="status">
                            <span className="t-xs t-tertiary">Şifre (bir kez):</span>{' '}
                            <span className="t-mono" data-testid={`mbx-password-${key}`}>{revealed.password}</span>
                            <button
                              type="button"
                              className="iconbtn"
                              data-testid={`mbx-copy-${key}`}
                              aria-label="Şifreyi kopyala"
                              onClick={() => void copyText(revealed.password)}
                            >
                              <Icon.Copy size={13} />
                            </button>
                            <button
                              type="button"
                              className="iconbtn"
                              aria-label="Gizle"
                              onClick={() => setRevealed(null)}
                            >
                              <Icon.EyeOff size={13} />
                            </button>
                          </div>
                        ) : null}
                      </td>
                      <td data-label="Durum">
                        <Badge tone={st.tone} data-testid={`mbx-status-${key}`}>{st.text}</Badge>
                        {!row.hasCredential ? (
                          <span className="t-xs t-tertiary" style={{ marginLeft: 6 }}>kimlik yok</span>
                        ) : null}
                      </td>
                      <td data-label="Oturum">
                        <span className="t-small">{row.verified ? 'doğrulandı' : row.sessionState}</span>
                      </td>
                      <td data-label="Kira">
                        <Badge tone={lease.tone} data-testid={`mbx-lease-${key}`}>{lease.text}</Badge>
                      </td>
                      <td data-label="Ard. hata">
                        {row.consecutiveErrors > 0 ? (
                          <span className="t-small">
                            {row.consecutiveErrors}
                            {row.cooldownUntil ? (
                              <span className="t-xs t-tertiary"> · tekrar {fmt(row.cooldownUntil)}</span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="t-tertiary">—</span>
                        )}
                      </td>
                      <td className="t-small t-tertiary" data-label="Son kontrol">{fmt(row.lastSeenAt)}</td>
                      <td data-label="Sunulan / Yanan siteler">
                        {row.burnedSites.length + row.servedSites.length === 0 ? (
                          <span className="t-tertiary">—</span>
                        ) : (
                          <span className="mbx-sites">
                            {row.burnedSites.map((s) => (
                              <Badge key={`b-${s}`} tone="danger" title="posta geldi — herkese kapalı">{s}</Badge>
                            ))}
                            {row.servedSites.map((s) => (
                              <Badge key={`s-${s}`} tone="warn" title="satıldı, posta gelmedi — yalnız o müşteriye kapalı">{s}</Badge>
                            ))}
                          </span>
                        )}
                      </td>
                      {canManage ? (
                        <td data-label="İşlemler" className="mbx-actions-cell">
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            data-testid={`mbx-actions-${key}`}
                            aria-haspopup="menu"
                            aria-expanded={openMenu === key}
                            onClick={() => setOpenMenu(openMenu === key ? null : key)}
                          >
                            İşlemler
                          </button>
                          {openMenu === key ? (
                            <div className="mbx-menu" role="menu" data-testid={`mbx-menu-${key}`}>
                              <button type="button" role="menuitem" className="mbx-menu-item" data-testid={`mbx-reveal-${key}`}
                                disabled={busy !== null || !row.hasCredential}
                                onClick={() => void doReveal(row)}>
                                <Icon.Key size={13} /> Şifre göster
                              </button>
                              <button type="button" role="menuitem" className="mbx-menu-item" data-testid={`mbx-changepw-${key}`}
                                disabled={busy !== null}
                                onClick={() => { setOpenMenu(null); setModal({ kind: 'password', row }); }}>
                                <Icon.Pencil size={13} /> Şifre değiştir
                              </button>
                              <button type="button" role="menuitem" className="mbx-menu-item" data-testid={`mbx-reprobe-${key}`}
                                disabled={busy !== null}
                                onClick={() => void doReprobe(row)}>
                                <Icon.Refresh size={13} /> Yeniden dene
                              </button>
                              {row.status === 'login_failed' ? (
                                <button type="button" role="menuitem" className="mbx-menu-item" data-testid={`mbx-resetq-${key}`}
                                  disabled={busy !== null}
                                  onClick={() => { setOpenMenu(null); setModal({ kind: 'quarantine', row }); }}>
                                  <Icon.Shield size={13} /> Karantinadan çıkar
                                </button>
                              ) : null}
                              <button type="button" role="menuitem" className="mbx-menu-item" data-testid={`mbx-sale-${key}`}
                                disabled={busy !== null}
                                onClick={() => { setOpenMenu(null); setModal({ kind: 'sale', row, onSale: row.status === 'disabled' }); }}>
                                {row.status === 'disabled' ? <Icon.Check size={13} /> : <Icon.Cancel size={13} />}
                                {row.status === 'disabled' ? ' Satışa aç' : ' Satıştan kaldır'}
                              </button>
                              <button type="button" role="menuitem" className="mbx-menu-item mbx-menu-danger" data-testid={`mbx-delete-${key}`}
                                disabled={busy !== null}
                                onClick={() => { setOpenMenu(null); setModal({ kind: 'delete', row }); }}>
                                <Icon.Trash size={13} /> Sil
                              </button>
                            </div>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>

      {modal?.kind === 'password' ? (
        <PasswordModal
          providerId={providerId}
          row={modal.row}
          busy={inputsDisabled}
          onClose={() => setModal(null)}
          onSubmit={async (input) => {
            const ok = await run(
              `changepw:${rowKey(modal.row)}`,
              (o) => updatePassword(o, providerId, modal.row.accountId, input),
              'Şifre güncellendi.',
            );
            if (ok) setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === 'delete' ? (
        <DeleteModal
          row={modal.row}
          canDrain={canDrain}
          busy={inputsDisabled}
          onClose={() => setModal(null)}
          onSoft={async (reason) => {
            const ok = await run(
              `delete:${rowKey(modal.row)}`,
              (o) => deleteMailbox(o, providerId, modal.row.accountId, { mode: 'soft', reason }),
              'Mailbox yumuşak silindi (kayıtlar korundu, satıştan kaldırıldı).',
            );
            if (ok) setModal(null);
          }}
          onHard={async (reason, confirmationToken) => {
            const ok = await run(
              `delete:${rowKey(modal.row)}`,
              (o) => deleteMailbox(o, providerId, modal.row.accountId, { mode: 'hard', reason, confirmationToken }),
              'Mailbox kalıcı olarak silindi.',
            );
            if (ok) setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === 'sale' ? (
        <ReasonModal
          testId="mbx-reason-modal"
          title={modal.onSale ? 'Satışa aç' : 'Satıştan kaldır'}
          body={
            modal.onSale
              ? `${modal.row.address} tekrar satışa açılacak ve yeniden doğrulanacak.`
              : `${modal.row.address} satıştan kaldırılacak (devre dışı). Tüm kayıtlar korunur; istediğinizde geri açabilirsiniz.`
          }
          confirmLabel={modal.onSale ? 'Satışa aç' : 'Satıştan kaldır'}
          danger={!modal.onSale}
          busy={inputsDisabled}
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            const ok = await run(
              `sale:${rowKey(modal.row)}`,
              (o) => setSaleStatus(o, providerId, modal.row.accountId, { onSale: modal.onSale, reason }),
              modal.onSale ? 'Mailbox satışa açıldı.' : 'Mailbox satıştan kaldırıldı.',
            );
            if (ok) setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === 'quarantine' ? (
        <ReasonModal
          testId="mbx-reason-modal"
          title="Karantinadan çıkar"
          body={`${modal.row.address} tekrar aktifleştirilecek; satışa açılmadan önce kimliği yeniden doğrulanır.`}
          confirmLabel="Karantinadan çıkar"
          busy={inputsDisabled}
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            const ok = await run(
              `resetq:${rowKey(modal.row)}`,
              (o) => resetQuarantine(o, providerId, modal.row.accountId, reason),
              'Karantinadan çıkarıldı — kimlik yeniden doğrulanacak.',
            );
            if (ok) setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === 'bulkTakeOff' ? (
        <ReasonModal
          testId="mbx-reason-modal"
          title="Seçilenleri satıştan kaldır"
          body={`${selectedIds.length} mailbox satıştan kaldırılacak. Rezerve/kirada olanlar atlanır (canlı sipariş korunur).`}
          confirmLabel="Satıştan kaldır"
          danger
          busy={inputsDisabled}
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            const ok = await run(
              'bulk:takeoff',
              (o) => bulkTakeOffSale(o, providerId, selectedIds, reason),
              'Seçilenler satıştan kaldırıldı (rezerve/kirada olanlar atlandı).',
            );
            if (ok) {
              setSelected(new Set());
              setModal(null);
            }
          }}
        />
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------

function ReasonModal({
  testId,
  title,
  body,
  confirmLabel,
  danger = false,
  busy,
  onConfirm,
  onClose,
}: {
  readonly testId: string;
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly danger?: boolean;
  readonly busy: boolean;
  readonly onConfirm: (reason: string) => void | Promise<void>;
  readonly onClose: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= MIN_REASON;
  return (
    <Modal open onClose={onClose} title={title} testId={testId} width={440}>
      <p className="t-small" style={{ marginTop: 0 }}>{body}</p>
      <label className="field-label" htmlFor={`${testId}-reason`}>Gerekçe (en az {MIN_REASON} karakter)</label>
      <Input
        id={`${testId}-reason`}
        data-testid="mbx-reason-input"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="kısa gerekçe"
        disabled={busy}
      />
      <div className="dialog-footer" style={{ marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Vazgeç</Button>
        <Button
          variant={danger ? 'danger' : 'primary'}
          data-testid="mbx-reason-confirm"
          disabled={!valid || busy}
          loading={busy}
          onClick={() => void onConfirm(reason.trim())}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

function PasswordModal({
  providerId,
  row,
  busy,
  onSubmit,
  onClose,
}: {
  readonly providerId: MailboxUnitProviderId;
  readonly row: MailboxRow;
  readonly busy: boolean;
  readonly onSubmit: (input: { password: string; extraSecret?: string }) => void | Promise<void>;
  readonly onClose: () => void;
}): JSX.Element {
  const isAol = providerId === 'aol';
  const [password, setPassword] = useState('');
  const [webPassword, setWebPassword] = useState('');
  const [phone, setPhone] = useState('');
  const valid = password.trim().length > 0;

  function submit(): void {
    if (!valid || busy) return;
    if (isAol) {
      onSubmit({ password: password.trim(), extraSecret: `web=${webPassword};phone=${phone}` });
    } else {
      onSubmit({ password });
    }
  }

  return (
    <Modal open onClose={onClose} title={`Şifre değiştir — ${row.address}`} testId="mbx-password-modal" width={460}>
      {isAol ? (
        <>
          <label className="field-label" htmlFor="mbx-pw-app">Uygulama şifresi (IMAP, 16 harf)</label>
          <Input
            id="mbx-pw-app"
            data-testid="mbx-pw-app"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
          <label className="field-label" htmlFor="mbx-pw-web" style={{ marginTop: 10 }}>Web şifresi</label>
          <Input
            id="mbx-pw-web"
            data-testid="mbx-pw-web"
            type="password"
            autoComplete="new-password"
            value={webPassword}
            onChange={(e) => setWebPassword(e.target.value)}
            disabled={busy}
          />
          <label className="field-label" htmlFor="mbx-pw-phone" style={{ marginTop: 10 }}>Telefon</label>
          <Input
            id="mbx-pw-phone"
            data-testid="mbx-pw-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={busy}
          />
          <p className="t-xs t-tertiary" style={{ marginTop: 8 }}>
            IMAP girişinde yalnız uygulama şifresi kullanılır; web şifresi ve telefon ek gizli alanda saklanır.
          </p>
        </>
      ) : (
        <>
          <label className="field-label" htmlFor="mbx-pw-input">Yeni şifre</label>
          <Input
            id="mbx-pw-input"
            data-testid="mbx-pw-input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
        </>
      )}
      <p className="t-xs t-tertiary" style={{ marginTop: 8 }}>
        Şifre kimlik kasasında yeniden şifrelenir; yanıtta veya kayıtlarda asla görünmez.
      </p>
      <div className="dialog-footer" style={{ marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Vazgeç</Button>
        <Button variant="primary" data-testid="mbx-pw-save" disabled={!valid || busy} loading={busy} onClick={submit}>
          Kaydet
        </Button>
      </div>
    </Modal>
  );
}

function DeleteModal({
  row,
  canDrain,
  busy,
  onSoft,
  onHard,
  onClose,
}: {
  readonly row: MailboxRow;
  readonly canDrain: boolean;
  readonly busy: boolean;
  readonly onSoft: (reason: string) => void | Promise<void>;
  readonly onHard: (reason: string, confirmationToken: string) => void | Promise<void>;
  readonly onClose: () => void;
}): JSX.Element {
  const hardAllowed = canDrain && isPristineRow(row) && row.accountId.length > 0;
  const [tab, setTab] = useState<'soft' | 'hard'>('soft');
  const [reason, setReason] = useState('');
  const [confirmToken, setConfirmToken] = useState('');
  const reasonValid = reason.trim().length >= MIN_REASON;
  const tokenValid = confirmToken === row.accountId;

  const tabs = hardAllowed
    ? [
        { id: 'soft', label: 'Yumuşak sil', testId: 'mbx-delete-tab-soft' },
        { id: 'hard', label: 'Kalıcı sil', testId: 'mbx-delete-tab-hard' },
      ]
    : [{ id: 'soft', label: 'Yumuşak sil', testId: 'mbx-delete-tab-soft' }];

  return (
    <Modal open onClose={onClose} title={`Sil — ${row.address}`} testId="mbx-delete-modal" width={480}>
      <Tabs tabs={tabs} activeId={tab} onSelect={(id) => setTab(id as 'soft' | 'hard')} testId="mbx-delete-tabs" />

      {tab === 'soft' ? (
        <div data-testid="mbx-delete-soft-pane">
          <p className="t-small" style={{ marginTop: 0 }}>
            <strong>Yumuşak sil:</strong> kayıtlar korunur, mailbox satıştan kaldırılır (devre dışı).
            Sipariş geçmişi, site kullanımı ve muhasebe bozulmaz. Rezerve/kirada bir mailbox silinemez.
          </p>
          <label className="field-label" htmlFor="mbx-delete-soft-reason">Gerekçe (en az {MIN_REASON} karakter)</label>
          <Input
            id="mbx-delete-soft-reason"
            data-testid="mbx-delete-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="kısa gerekçe"
            disabled={busy}
          />
          <div className="dialog-footer" style={{ marginTop: 16 }}>
            <Button variant="ghost" onClick={onClose} disabled={busy}>Vazgeç</Button>
            <Button
              variant="danger"
              data-testid="mbx-delete-soft"
              disabled={!reasonValid || busy}
              loading={busy}
              onClick={() => void onSoft(reason.trim())}
            >
              Yumuşak sil
            </Button>
          </div>
        </div>
      ) : (
        <div data-testid="mbx-delete-hard-pane">
          <p className="t-small" style={{ marginTop: 0, color: 'var(--danger-600, #dc2626)' }}>
            <strong>Kalıcı sil:</strong> geri alınamaz. Yalnız hiç satılmamış/kullanılmamış (tertemiz)
            bir mailbox için mümkündür ve sunucu bunu ayrıca doğrular. Onaylamak için hesap kimliğini yazın.
          </p>
          <label className="field-label" htmlFor="mbx-delete-hard-reason">Gerekçe (en az {MIN_REASON} karakter)</label>
          <Input
            id="mbx-delete-hard-reason"
            data-testid="mbx-delete-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="kısa gerekçe"
            disabled={busy}
          />
          <label className="field-label" htmlFor="mbx-delete-confirm-input" style={{ marginTop: 10 }}>
            Onay: hesap kimliğini yazın (<span className="t-mono">{row.accountId}</span>)
          </label>
          <Input
            id="mbx-delete-confirm-input"
            data-testid="mbx-delete-confirm-input"
            value={confirmToken}
            onChange={(e) => setConfirmToken(e.target.value)}
            placeholder={row.accountId}
            disabled={busy}
          />
          <div className="dialog-footer" style={{ marginTop: 16 }}>
            <Button variant="ghost" onClick={onClose} disabled={busy}>Vazgeç</Button>
            <Button
              variant="danger"
              data-testid="mbx-delete-hard"
              disabled={!reasonValid || !tokenValid || busy}
              loading={busy}
              onClick={() => void onHard(reason.trim(), confirmToken)}
            >
              Kalıcı sil
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
